/**
 * What kind of value an expression gives — whole number, decimal, character,
 * text, yes/no — from the expression keyboard's parse tree and the types in its
 * environment (ExpressionKeyboard/engine.js, env.js). Pure: no React, no store.
 *
 * The rules are C's, said in words:
 *   whole op whole → whole          (7 ÷ 2 is 3)
 *   any decimal    → decimal
 *   a character in a sum → whole    ('a' + 1 is 98)
 *   a comparison, and / or / not → yes/no
 *   "Ravi" → text, 'A' → character
 * Anything it cannot tell (an unknown function, a field of a field it has no
 * type for) is null: "don't know", never a guess.
 *
 * Also the wrong-type guard a step uses before it stores a value:
 * `assignCheck('whole', 'text', 'total')` → "total holds a whole number; this gives text".
 */
import { tokenize, parse, createEnv } from '@/components/visual-flow-builder/ExpressionKeyboard/engine';
import { kindOfDataType } from './inputReads';

export const VALUE_KINDS = ['whole', 'decimal', 'character', 'text', 'yesno'];

/** "a whole number", "text", … — the kind as a sentence uses it. */
export const KIND_PHRASE = {
  whole: 'a whole number',
  decimal: 'a decimal',
  character: 'a character',
  text: 'text',
  yesno: 'a yes/no',
  list: 'a list',
  record: 'a record',
  address: 'an address',
};
/** The kind on a tag: "decimal", "whole number". */
export const KIND_TAG = {
  whole: 'whole number',
  decimal: 'decimal',
  character: 'character',
  text: 'text',
  yesno: 'yes/no',
  list: 'list',
  record: 'record',
  address: 'address',
};
export const kindPhrase = (k) => KIND_PHRASE[k] || '';
export const kindTag = (k) => KIND_TAG[k] || '';

/** The C type a new single value of a kind is declared as (as the Input form does). */
export const KIND_C_TYPE = { whole: 'int', decimal: 'float', character: 'char', yesno: 'bool' };

/** The kind a C type holds: `int` → whole, `char[]` → text, `int[]` → list, `struct S` → record. */
export function kindOfCType(type, resources = []) {
  const t = String(type || '').trim().replace(/\s+/g, ' ');
  if (!t) return null;
  if (/^(const )?char ?(\[\]|\*)$/.test(t) || /^char\[\d*\]$/.test(t)) return 'text';
  if (/\[\]$/.test(t)) return 'list';
  if (/\*$/.test(t)) return 'address';
  if (/^(struct|union) /.test(t)) return 'record';
  return kindOfDataType(t, resources);
}

// What the C library calls a student meets give back.
const LIB_RETURNS = {
  sqrt: 'double', pow: 'double', fabs: 'double', floor: 'double', ceil: 'double', round: 'double',
  sin: 'double', cos: 'double', tan: 'double', log: 'double', log10: 'double', exp: 'double',
  fmod: 'double', atof: 'double', fmin: 'double', fmax: 'double', trunc: 'double',
  abs: 'int', labs: 'long', strlen: 'size_t', strcmp: 'int', strncmp: 'int', atoi: 'int', atol: 'long',
  rand: 'int', toupper: 'int', tolower: 'int', isdigit: 'bool', isalpha: 'bool', isalnum: 'bool',
  isspace: 'bool', isupper: 'bool', islower: 'bool', ispunct: 'bool', getchar: 'int', fgetc: 'int',
  strcpy: 'char*', strcat: 'char*', strncpy: 'char*', strncat: 'char*', strchr: 'char*', strstr: 'char*',
  fopen: 'FILE*', malloc: 'void*', calloc: 'void*', realloc: 'void*',
};
const BUILTIN_TYPES = {
  NULL: 'void*', EOF: 'int', true: 'bool', false: 'bool', RAND_MAX: 'int', INT_MAX: 'int', INT_MIN: 'int',
  LONG_MAX: 'long', LONG_MIN: 'long', CHAR_MAX: 'int', CHAR_MIN: 'int', UINT_MAX: 'unsigned int',
  SEEK_SET: 'int', SEEK_CUR: 'int', SEEK_END: 'int', EXIT_SUCCESS: 'int', EXIT_FAILURE: 'int', BUFSIZ: 'int',
};

const ARITH = ['+', '-', '*', '/', '%'];
const COMPARE = ['==', '!=', '<', '>', '<=', '>=', '&&', '||'];
const BITWISE = ['&', '|', '^', '<<', '>>'];
const isDecimalType = (t) => /^(float|double|long double)$/.test(String(t || '').trim());
const isWholeLike = (t) => {
  const k = kindOfCType(t);
  return k === 'whole' || k === 'character' || k === 'yesno';
};
const stripOne = (t) => {
  const s = String(t || '').trim();
  if (/\[\]$/.test(s)) return s.slice(0, -2).trim();
  if (/\*$/.test(s)) return s.slice(0, -1).trim();
  return null;
};

/** The fields of the record a subtree names, from the env (null when unknown). */
function fieldsOf(n, env) {
  if (!n) return null;
  if (n.t === 'group') return fieldsOf(n.x, env);
  if (n.t === 'var') { const v = env.vars.get(n.v); return (v && v.fields) || null; }
  if (n.t === 'index' && n.x.t === 'var') { const v = env.vars.get(n.x.v); return (v && v.elementFields) || null; }
  if (n.t === 'deref') return fieldsOf(n.x, env);
  return null;
}

/** The C type of a parse-tree node, or null. */
export function typeOfTree(n, env = createEnv()) {
  if (!n) return null;
  switch (n.t) {
    case 'num': {
      const v = String(n.v);
      if (/^0[xX]/.test(v)) return 'int';
      return /[.eE]|[fF]$/.test(v) ? 'double' : 'int';
    }
    case 'chr': return 'char';
    case 'str': return 'char*';
    case 'var': {
      const v = env.vars.get(n.v);
      if (v) return v.type || null;
      const c = env.consts.get(n.v);
      if (!c) return null;
      if (c.builtin) return BUILTIN_TYPES[n.v] || null;
      if (c.type && env.types && env.types.has(c.type)) return 'int'; // an enum member
      return c.type || null;
    }
    case 'group': return typeOfTree(n.x, env);
    case 'index': return stripOne(typeOfTree(n.x, env));
    case 'deref': return stripOne(typeOfTree(n.x, env));
    case 'addr': { const t = typeOfTree(n.x, env); return t ? `${t}*` : 'void*'; }
    case 'member': {
      const fields = fieldsOf(n.x, env);
      const f = fields && fields.find((x) => x && x.name === n.name);
      return (f && f.type) || null;
    }
    case 'cast': return n.ty || null;
    case 'neg': { const t = typeOfTree(n.x, env); return t && isDecimalType(t) ? t : (t ? 'int' : null); }
    case 'not': return 'bool';
    case 'bitnot': return 'int';
    case 'size': case 'sizeType': return 'size_t';
    case 'choose': {
      const a = typeOfTree(n.a, env);
      const b = typeOfTree(n.b, env);
      if (isDecimalType(a) || isDecimalType(b)) return 'double';
      return a || b;
    }
    case 'call': {
      const info = env.functions && Object.prototype.hasOwnProperty.call(env.functions, n.fn) ? env.functions[n.fn] : null;
      if (info && info.returnType) return info.returnType;
      return LIB_RETURNS[n.fn] || null;
    }
    case 'bin': {
      if (COMPARE.includes(n.op)) return 'bool';
      if (BITWISE.includes(n.op)) return 'int';
      if (ARITH.includes(n.op)) {
        const l = typeOfTree(n.l, env);
        const r = typeOfTree(n.r, env);
        if (n.op === '%') return 'int';
        if (isDecimalType(l) || isDecimalType(r)) return 'double';
        const lk = kindOfCType(l);
        const rk = kindOfCType(r);
        // Moving along a text or an address: still that.
        if ((lk === 'address' || lk === 'text') && isWholeLike(r) && (n.op === '+' || n.op === '-')) return l;
        if (lk === 'address' && rk === 'address' && n.op === '-') return 'long';
        if (isWholeLike(l) && isWholeLike(r)) return 'int';
        return null;
      }
      return null;
    }
    default: return null;
  }
}

/** The kind of a parse tree: 'whole' | 'decimal' | 'character' | 'text' | 'yesno' | 'list' | 'record' | 'address' | null. */
export function inferKind(tree, env = createEnv()) {
  return kindOfCType(typeOfTree(tree, env));
}

/** The kind of a C expression, or null when it does not parse or cannot be told. */
export function kindOfC(c, env = createEnv()) {
  const r = parse(tokenize(c, env), env);
  return r.tree ? inferKind(r.tree, env) : null;
}

const NUMBERISH = ['whole', 'character', 'yesno'];

/**
 * Can a value of kind `value` be stored in something of kind `target`?
 *   → { ok: true } | { ok: true, warning } | { ok: false, error }
 * Unknown kinds (null) always pass: the guard never refuses on a guess.
 * Whole → decimal is fine; decimal → whole is allowed, as C allows it, with a
 * plain warning that the decimals are dropped.
 */
export function assignCheck(target, value, name = 'it', verb = 'holds') {
  if (!target || !value || target === value) return { ok: true };
  const holds = `${name} ${verb} ${kindPhrase(target)}`;
  const gives = `this gives ${kindPhrase(value)}`;
  if (target === 'text' || value === 'text' || ['list', 'record'].includes(target) || ['list', 'record'].includes(value)) {
    return { ok: false, error: `${holds}; ${gives}.` };
  }
  if (target === 'address' || value === 'address') {
    return target === value ? { ok: true } : { ok: false, error: `${holds}; ${gives}.` };
  }
  if (value === 'decimal' && NUMBERISH.includes(target)) {
    return { ok: true, warning: `${holds}, so the decimals are dropped.` };
  }
  return { ok: true };
}

// ---- the address and record checks (they need the C types, not just the kinds) ----
export const tagOf = (t) => String(t || '').trim().replace(/^(const\s+)/, '').replace(/^(struct|union)[_\s]+/, '').trim();
export const bareType = (t) => String(t || '').replace(/\bconst\b/g, '').replace(/^(struct|union)_/, '$1 ')
  .replace(/\b(un)?signed\s+(?=char\b)/g, '').replace(/\s+/g, ' ').replace(/\s*\*/g, '*').trim();
/** What a type is, in words: "a whole number", "a Node", "an address". */
export const typePhrase = (t) => {
  const s = String(t || '').trim();
  if (/^(struct|union)[_\s]/.test(s)) return `a ${tagOf(s)}`;
  return kindPhrase(kindOfCType(s)) || `a ${s}`;
};
export const isZero = (n) => !!n && ((n.t === 'num' && /^0+[uUlL]*$/.test(String(n.v))) || (n.t === 'group' && isZero(n.x)));

/**
 * The guard with the C types in hand, for what the kinds alone cannot say:
 *   - an address takes only an address of the same kind of thing — `&x`, a
 *     pointer, a list (it decays to the address of its first item), a call
 *     that gives one back (malloc), NULL or a plain 0;
 *   - a record takes only a record of the same type;
 *   - a char* (text) can also be NULL or 0.
 * Anything else goes to assignCheck. Unknown types always pass.
 *   → { ok: true } | { ok: true, warning } | { ok: false, error }
 */
export function assignCheckTree({ targetKind, targetType = null, tree, env = createEnv(), name = 'it', verb = 'holds' }) {
  if (!targetKind || !tree) return { ok: true };
  const vt = typeOfTree(tree, env);
  const vk = kindOfCType(vt);
  const holds = `${name} ${verb}`;
  const isPointerTarget = /\*\s*$/.test(String(targetType || '').trim());
  if (targetKind === 'address' || (targetKind === 'text' && isPointerTarget)) {
    if (isZero(tree) || (tree.t === 'var' && tree.v === 'NULL')) return { ok: true };
    if (targetKind === 'text') return assignCheck(targetKind, vk, name, verb);
    if (!vk) return { ok: true };
    const pointee = bareType(String(targetType || '').replace(/\*\s*$/, ''));
    let given = null;
    if (vk === 'address') given = bareType(String(vt).replace(/\*\s*$/, ''));
    else if (vk === 'list' || vk === 'text') given = bareType(String(vt).replace(/(\[\]|\*)\s*$/, ''));
    else return { ok: false, error: `${holds} an address; this gives ${kindPhrase(vk) || 'a value'}.` };
    if (!targetType || !pointee || !given || pointee === 'void' || given === 'void') return { ok: true };
    if (pointee === given || tagOf(pointee) === tagOf(given)) return { ok: true };
    return { ok: false, error: `${holds} the address of ${typePhrase(pointee)}; this gives the address of ${typePhrase(given)}.` };
  }
  if (targetKind === 'record') {
    if (!vk) return { ok: true };
    if (vk !== 'record') return { ok: false, error: `${holds} a whole record; this gives ${kindPhrase(vk) || 'a value'}.` };
    if (!targetType || !vt || tagOf(targetType) === tagOf(vt)) return { ok: true };
    return { ok: false, error: `${holds} ${typePhrase(targetType)} record; this gives ${typePhrase(vt)} record.` };
  }
  return assignCheck(targetKind, vk, name, verb);
}
